/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useAuth } from "../auth/AuthProvider";
import {
  applyQuoteResponse,
  applyQuoteSend,
  calculatePaymentState,
  calculateQuoteTotals,
} from "../lib/quotes";
import { applyPhaseStatus } from "../lib/work-items";
import { projectFileMimeType, validateProjectFile, validateProjectFileContents } from "../lib/files";
import { getSupabaseAccessToken, supabase } from "../lib/supabase";
import type {
  MutableEntity,
  PaymentInput,
  PhaseStatus,
  QuoteDraftInput,
  QuoteResponse,
  QuoteStatus,
  WorkItemStatus,
  WorkspaceData,
} from "../types";
import { resolveTenant } from "./tenant";
import { classifyLocation } from "../config/domains";

interface WorkspaceState {
  data: WorkspaceData | null;
  loading: boolean;
  error: string | null;
  notice: string | null;
  refresh(): Promise<void>;
  save(
    entity: MutableEntity,
    record: Record<string, unknown>,
  ): Promise<boolean>;
  archive(entity: MutableEntity, id: string): Promise<boolean>;
  setCollaborationArchived(
    entity: "work_items" | "decisions" | "change_requests" | "meetings",
    id: string,
    archived: boolean,
  ): Promise<boolean>;
  deleteCollaborationRecord(
    entity: "work_items" | "decisions" | "change_requests" | "meetings",
    id: string,
  ): Promise<boolean>;
  moveWorkItem(id: string, status: WorkItemStatus): Promise<boolean>;
  completeWaitingClient(id: string): Promise<boolean>;
  addWorkItemComment(workItemId: string, body: string): Promise<boolean>;
  addChangeRequestComment(
    changeRequestId: string,
    body: string,
  ): Promise<boolean>;
  setPhaseStatus(id: string, status: PhaseStatus): Promise<boolean>;
  savePhase(record: {
    id?: string;
    name: string;
    description: string;
    start_date: string;
    end_date: string;
    status: PhaseStatus;
    position: number;
    calendar_color: string;
  }): Promise<boolean>;
  saveQuoteDraft(input: QuoteDraftInput): Promise<boolean>;
  sendQuote(
    quoteId: string,
    notify: boolean,
    recipients: string[],
  ): Promise<boolean>;
  respondToQuote(
    quoteId: string,
    response: QuoteResponse,
    comment: string,
    requestType?: string,
  ): Promise<boolean>;
  createQuoteRevision(quoteId: string): Promise<boolean>;
  registerPayment(quoteId: string, payment: PaymentInput): Promise<boolean>;
  archiveQuote(quoteId: string): Promise<boolean>;
  adminSetQuoteStatus(
    quoteId: string,
    status: QuoteStatus,
    reason: string,
  ): Promise<boolean>;
  downloadQuotePdf(
    quoteId: string,
    quoteNumber?: string | null,
  ): Promise<boolean>;
  setEmailNotificationsEnabled(enabled: boolean): Promise<boolean>;
  uploadFile(
    file: File,
    category: string,
    description: string,
    changeRequestId?: string | null,
    folderId?: string | null,
  ): Promise<boolean>;
  downloadFile(storagePath: string, fileName?: string): Promise<boolean>;
  inviteMember(email: string, role: string): Promise<boolean>;
  clearNotice(): void;
}

const WorkspaceContext = createContext<WorkspaceState | null>(null);
const localKey = "positronico-local-workspace-v3";

const writableFields: Record<MutableEntity, string[]> = {
  work_items: [
    "organization_id",
    "project_id",
    "title",
    "description",
    "status",
    "assignee_id",
    "due_date",
    "priority",
    "archived_at",
  ],
  project_phases: [
    "organization_id",
    "project_id",
    "name",
    "description",
    "start_date",
    "end_date",
    "status",
    "position",
    "calendar_color",
    "archived_at",
  ],
  meetings: [
    "organization_id",
    "project_id",
    "title",
    "starts_at",
    "date_window_start",
    "date_window_end",
    "timezone",
    "objective",
    "agenda",
    "meeting_url",
    "notes",
    "status",
    "archived_at",
  ],
  milestones: [
    "organization_id",
    "project_id",
    "name",
    "description",
    "date",
    "type",
    "status",
    "archived_at",
  ],
  decisions: [
    "organization_id",
    "project_id",
    "text",
    "context",
    "status",
    "change_request_id",
    "resolution_note",
    "decided_at",
    "archived_at",
  ],
  notes: ["organization_id", "project_id", "title", "body", "archived_at"],
  change_requests: [
    "organization_id",
    "project_id",
    "title",
    "description",
    "category",
    "priority",
    "status",
    "archived_at",
  ],
  organization_members: [
    "organization_id",
    "user_id",
    "email",
    "email_verified_at",
    "role",
    "status",
    "invited_by",
    "disabled_at",
  ],
  files: [
    "organization_id",
    "project_id",
    "change_request_id",
    "name",
    "category",
    "description",
    "storage_path",
    "size_bytes",
    "mime_type",
    "archived_at",
  ],
};

function toDatabaseRecord(
  entity: MutableEntity,
  record: Record<string, unknown>,
) {
  return Object.fromEntries(
    writableFields[entity]
      .filter((field) => field in record)
      .map((field) => [field, record[field]]),
  );
}

function isWorkspaceData(value: unknown): value is WorkspaceData {
  if (!value || typeof value !== "object") return false;
  const object = value as Record<string, unknown>;
  return Boolean(
    object.organization &&
      object.project &&
      Array.isArray(object.workItems) &&
      Array.isArray(object.quotes) &&
      Array.isArray(object.agents),
  );
}

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { user, localDemo } = useAuth();
  const [data, setData] = useState<WorkspaceData | null>(null);
  const [loading, setLoading] = useState(Boolean(user));
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const surface = classifyLocation(
    window.location,
    import.meta.env.VITE_DEFAULT_TENANT,
  );
  const tenant =
    surface.kind === "tenant"
      ? surface.slug
      : resolveTenant(
          window.location.hostname,
          import.meta.env.VITE_DEFAULT_TENANT,
        );

  const persistLocal = useCallback((next: WorkspaceData) => {
    setData(next);
    localStorage.setItem(localKey, JSON.stringify(next));
  }, []);

  const refresh = useCallback(async () => {
    if (!user) {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      if (localDemo) {
        const saved = localStorage.getItem(localKey);
        const parsed: unknown = saved ? JSON.parse(saved) : null;
        if (isWorkspaceData(parsed))
          setData({ ...parsed, changeComments: parsed.changeComments ?? [] });
        else {
          const response = await fetch("/bootstrap-local.json");
          if (!response.ok)
            throw new Error("No se encontró el bootstrap local.");
          const initial: unknown = await response.json();
          if (!isWorkspaceData(initial))
            throw new Error("El bootstrap local no es válido.");
          persistLocal({ ...initial, changeComments: initial.changeComments ?? [] });
        }
      } else if (supabase) {
        const { data: snapshot, error: snapshotError } = await supabase.rpc(
          "get_workspace_snapshot",
          { p_tenant_slug: tenant },
        );
        if (snapshotError) throw snapshotError;
        if (!isWorkspaceData(snapshot))
          throw new Error(
            "La organización no está disponible para este usuario.",
          );
        const { data: changeDetails, error: detailsError } = await supabase.rpc(
          "get_change_request_details",
          { p_project_id: snapshot.project.id },
        );
        if (detailsError) throw detailsError;
        const { data: workItems, error: workItemsError } = await supabase.rpc(
          "get_project_work_items",
          { p_project_id: snapshot.project.id },
        );
        if (workItemsError) throw workItemsError;
        const details = changeDetails && typeof changeDetails === "object"
          ? changeDetails as Record<string, unknown>
          : {};
        const requesters = Array.isArray(details.requesters) ? details.requesters : [];
        const requesterByChange = new Map(
          requesters
            .filter((value): value is Record<string, unknown> => Boolean(value) && typeof value === "object")
            .map((value) => [String(value.change_request_id), value]),
        );
        setData({
          ...snapshot,
          workItems: Array.isArray(workItems) ? workItems : [],
          changes: snapshot.changes.map((change) => {
            const requester = requesterByChange.get(change.id);
            return requester
              ? { ...change, requester_id: String(requester.requester_id), requester_name: String(requester.requester_name) }
              : change;
          }),
          changeComments: Array.isArray(details.comments) ? details.comments : [],
        });
      }
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "No pudimos cargar el proyecto.",
      );
    } finally {
      setLoading(false);
    }
  }, [user, localDemo, persistLocal, tenant]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const save = useCallback(
    async (entity: MutableEntity, record: Record<string, unknown>) => {
      if (!data) return false;
      try {
        const normalized: Record<string, unknown> = {
          ...record,
          organization_id: record.organization_id ?? data.organization.id,
        };
        if (entity !== "organization_members")
          normalized.project_id = record.project_id ?? data.project.id;
        if (typeof normalized.id !== "string" || !normalized.id)
          delete normalized.id;
        if (localDemo) {
          if ((entity === "meetings" || entity === "decisions") && data.viewer.role !== "POSITRONICO_OWNER") return false;
          if (entity === "change_requests") {
            if (normalized.id && data.viewer.role !== "POSITRONICO_OWNER") return false;
            if (!normalized.id) normalized.status = "NEW";
          }
          const map: Record<MutableEntity, keyof WorkspaceData> = {
            work_items: "workItems",
            project_phases: "phases",
            meetings: "meetings",
            milestones: "milestones",
            decisions: "decisions",
            notes: "notes",
            change_requests: "changes",
            organization_members: "members",
            files: "files",
          };
          const key = map[entity];
          const current = data[key];
          if (!Array.isArray(current)) return false;
          const id =
            typeof record.id === "string" ? record.id : crypto.randomUUID();
          const index = current.findIndex(
            (item) =>
              typeof item === "object" &&
              item !== null &&
              "id" in item &&
              item.id === id,
          );
          const localRecord = { ...normalized, id };
          const nextList =
            index >= 0
              ? current.map((item, itemIndex) =>
                  itemIndex === index ? { ...item, ...localRecord } : item,
                )
              : [localRecord, ...current];
          persistLocal({ ...data, [key]: nextList } as WorkspaceData);
        } else if (supabase && entity === "decisions") {
          const { error: decisionError } = await supabase.rpc("save_decision", {
            p_decision_id:
              typeof normalized.id === "string" ? normalized.id : null,
            p_project_id: normalized.project_id,
            p_text: normalized.text,
            p_context: normalized.context ?? "",
            p_status: normalized.status ?? "APPROVED",
            p_change_request_id: normalized.change_request_id ?? null,
            p_resolution_note: normalized.resolution_note ?? "",
            p_decided_at:
              normalized.decided_at ?? new Date().toISOString().slice(0, 10),
          });
          if (decisionError) throw decisionError;
          await refresh();
        } else if (supabase && entity === "change_requests") {
          const { error: changeError } = await supabase.rpc("save_change_request", {
            p_change_request_id: typeof normalized.id === "string" ? normalized.id : null,
            p_project_id: normalized.project_id,
            p_title: normalized.title,
            p_description: normalized.description,
            p_category: normalized.category ?? "Producto",
            p_priority: normalized.priority ?? "MEDIUM",
            p_status: normalized.status ?? null,
          });
          if (changeError) throw changeError;
          await refresh();
        } else if (supabase && entity === "meetings") {
          const { error: meetingError } = await supabase.rpc("save_meeting", {
            p_meeting_id: typeof normalized.id === "string" ? normalized.id : null,
            p_project_id: normalized.project_id,
            p_title: normalized.title,
            p_starts_at: normalized.starts_at ?? null,
            p_date_window_start: normalized.date_window_start ?? null,
            p_date_window_end: normalized.date_window_end ?? null,
            p_timezone: normalized.timezone ?? "America/Mexico_City",
            p_objective: normalized.objective ?? "",
            p_agenda: normalized.agenda ?? "",
            p_meeting_url: normalized.meeting_url ?? null,
            p_notes: normalized.notes ?? "",
            p_status: normalized.status ?? "Por confirmar",
          });
          if (meetingError) throw meetingError;
          await refresh();
        } else if (supabase) {
          const recordId =
            typeof normalized.id === "string" ? normalized.id : null;
          const databaseRecord = toDatabaseRecord(entity, normalized);
          const result = recordId
            ? await supabase
                .from(entity)
                .update(databaseRecord)
                .eq("id", recordId)
            : await supabase.from(entity).insert(databaseRecord);
          if (result.error) throw result.error;
          await refresh();
        }
        setNotice("Cambios guardados.");
        return true;
      } catch (reason) {
        setNotice(
          reason instanceof Error ? reason.message : "No se pudo guardar.",
        );
        return false;
      }
    },
    [data, localDemo, persistLocal, refresh],
  );

  const archive = useCallback(
    async (entity: MutableEntity, id: string) =>
      save(entity, { id, archived_at: new Date().toISOString() }),
    [save],
  );

  const setCollaborationArchived = useCallback(
    async (
      entity: "work_items" | "decisions" | "change_requests" | "meetings",
      id: string,
      archived: boolean,
    ) => {
      if (!data) return false;
      try {
        if (localDemo) {
          const map = {
            work_items: "workItems",
            decisions: "decisions",
            change_requests: "changes",
            meetings: "meetings",
          } as const;
          const key = map[entity];
          persistLocal({
            ...data,
            [key]: data[key].map((item) =>
              item.id === id
                ? { ...item, archived_at: archived ? new Date().toISOString() : null }
                : item,
            ),
          });
        } else if (supabase) {
          const { error: archiveError } = await supabase.rpc(
            "set_collaboration_archived",
            { p_entity: entity, p_id: id, p_archived: archived },
          );
          if (archiveError) throw archiveError;
          await refresh();
        }
        setNotice(archived ? "Registro archivado." : "Registro restaurado.");
        return true;
      } catch (reason) {
        setNotice(reason instanceof Error ? reason.message : "No se pudo actualizar el registro.");
        return false;
      }
    },
    [data, localDemo, persistLocal, refresh],
  );

  const deleteCollaborationRecord = useCallback(
    async (
      entity: "work_items" | "decisions" | "change_requests" | "meetings",
      id: string,
    ) => {
      if (!data) return false;
      try {
        if (localDemo) {
          const map = {
            work_items: "workItems",
            decisions: "decisions",
            change_requests: "changes",
            meetings: "meetings",
          } as const;
          const key = map[entity];
          persistLocal({ ...data, [key]: data[key].filter((item) => item.id !== id) });
        } else if (supabase) {
          const { error: deleteError } = await supabase.rpc(
            "delete_collaboration_record",
            { p_entity: entity, p_id: id },
          );
          if (deleteError) throw deleteError;
          await refresh();
        }
        setNotice("Registro eliminado.");
        return true;
      } catch (reason) {
        setNotice(reason instanceof Error ? reason.message : "No se pudo eliminar el registro.");
        return false;
      }
    },
    [data, localDemo, persistLocal, refresh],
  );
  const moveWorkItem = useCallback(
    async (id: string, status: WorkItemStatus) => {
      if (data?.viewer.role !== "POSITRONICO_OWNER") {
        setNotice("La Pizarra sólo puede editarla el Owner.");
        return false;
      }
      return save("work_items", { id, status });
    },
    [data?.viewer.role, save],
  );

  const completeWaitingClient = useCallback(
    async (id: string) => {
      if (!data) return false;
      try {
        if (localDemo)
          persistLocal({
            ...data,
            workItems: data.workItems.map((item) =>
              item.id === id && item.status === "WAITING_CLIENT"
                ? { ...item, status: "TO_VALIDATE" }
                : item,
            ),
          });
        else if (supabase) {
          const { error: completeError } = await supabase.rpc(
            "complete_waiting_client_item",
            { p_work_item_id: id },
          );
          if (completeError) throw completeError;
          await refresh();
        }
        setNotice("Entrega registrada para validación de Positrónico.");
        return true;
      } catch (reason) {
        setNotice(
          reason instanceof Error
            ? reason.message
            : "No se pudo completar el pendiente.",
        );
        return false;
      }
    },
    [data, localDemo, persistLocal, refresh],
  );

  const addWorkItemComment = useCallback(
    async (workItemId: string, body: string) => {
      if (!data || !body.trim()) return false;
      try {
        if (localDemo)
          persistLocal({
            ...data,
            workItems: data.workItems.map((item) =>
              item.id === workItemId
                ? { ...item, comment_count: item.comment_count + 1 }
                : item,
            ),
          });
        else if (supabase) {
          const { error: commentError } = await supabase
            .from("work_item_comments")
            .insert({
              organization_id: data.organization.id,
              project_id: data.project.id,
              work_item_id: workItemId,
              body: body.trim(),
            });
          if (commentError) throw commentError;
          await refresh();
        }
        setNotice("Comentario agregado.");
        return true;
      } catch (reason) {
        setNotice(
          reason instanceof Error
            ? reason.message
            : "No se pudo agregar el comentario.",
        );
        return false;
      }
    },
    [data, localDemo, persistLocal, refresh],
  );

  const addChangeRequestComment = useCallback(
    async (changeRequestId: string, body: string) => {
      if (!data || !body.trim()) return false;
      try {
        if (!localDemo && supabase) {
          const { error: commentError } = await supabase
            .from("change_request_comments")
            .insert({
              organization_id: data.organization.id,
              project_id: data.project.id,
              change_request_id: changeRequestId,
              body: body.trim(),
            });
          if (commentError) throw commentError;
          await refresh();
        }
        setNotice("Comentario agregado al cambio.");
        return true;
      } catch (reason) {
        setNotice(
          reason instanceof Error
            ? reason.message
            : "No se pudo agregar el comentario.",
        );
        return false;
      }
    },
    [data, localDemo, refresh],
  );

  const setPhaseStatus = useCallback(
    async (id: string, status: PhaseStatus) => {
      if (!data) return false;
      try {
        if (localDemo) {
          const selected = data.phases.find((phase) => phase.id === id);
          if (!selected) return false;
          const phases = applyPhaseStatus(data.phases, id, status);
          persistLocal({ ...data, phases });
        } else if (supabase) {
          const { error: phaseError } = await supabase.rpc(
            "set_project_phase_status",
            { p_phase_id: id, p_status: status },
          );
          if (phaseError) throw phaseError;
          await refresh();
        }
        setNotice("Fase actualizada.");
        return true;
      } catch (reason) {
        setNotice(
          reason instanceof Error
            ? reason.message
            : "No se pudo actualizar la fase.",
        );
        return false;
      }
    },
    [data, localDemo, persistLocal, refresh],
  );

  const savePhase = useCallback(
    async (record: {
      id?: string;
      name: string;
      description: string;
      start_date: string;
      end_date: string;
      status: PhaseStatus;
      position: number;
      calendar_color: string;
    }) => {
      if (!data || data.viewer.role !== "POSITRONICO_OWNER") return false;
      try {
        if (localDemo) {
          const id = record.id ?? crypto.randomUUID();
          const phases = applyPhaseStatus(data.phases, id, record.status);
          const index = phases.findIndex((phase) => phase.id === id);
          const phase = {
            ...record,
            id,
            project_id: data.project.id,
            archived_at: null,
          };
          persistLocal({
            ...data,
            phases:
              index >= 0
                ? phases.map((item) =>
                    item.id === id ? { ...item, ...phase } : item,
                  )
                : [...phases, phase],
          });
        } else if (supabase) {
          const { error: phaseError } = await supabase.rpc(
            "save_project_phase",
            {
              p_phase_id: record.id ?? null,
              p_project_id: data.project.id,
              p_name: record.name,
              p_description: record.description,
              p_start_date: record.start_date,
              p_end_date: record.end_date,
              p_status: record.status,
              p_position: record.position,
              p_calendar_color: record.calendar_color,
            },
          );
          if (phaseError) throw phaseError;
          await refresh();
        }
        setNotice("Fase guardada.");
        return true;
      } catch (reason) {
        setNotice(
          reason instanceof Error
            ? reason.message
            : "No se pudo guardar la fase.",
        );
        return false;
      }
    },
    [data, localDemo, persistLocal, refresh],
  );

  const saveQuoteDraft = useCallback(
    async (input: QuoteDraftInput) => {
      if (
        !data ||
        data.viewer.role !== "POSITRONICO_OWNER" ||
        input.items.length === 0
      )
        return false;
      try {
        if (localDemo) {
          const now = new Date().toISOString();
          const quoteId = input.quote_id ?? crypto.randomUUID();
          const existing = data.quotes.find((quote) => quote.id === quoteId);
          const versionId = existing?.current_version_id ?? crypto.randomUUID();
          const versionNumber = existing?.current_version_number ?? 1;
          const previousVersion = data.quoteVersions.find(
            (version) => version.id === versionId,
          );
          if (previousVersion && previousVersion.status !== "DRAFT")
            throw new Error(
              "Una versión enviada es inmutable. Crea una revisión.",
            );
          const quote = existing
            ? {
                ...existing,
                change_request_id: input.change_request_id ?? null,
                status: "DRAFT" as QuoteStatus,
                updated_at: now,
              }
            : {
                id: quoteId,
                organization_id: data.organization.id,
                project_id: data.project.id,
                change_request_id: input.change_request_id ?? null,
                quote_number: null,
                status: "DRAFT" as QuoteStatus,
                payment_status: "PENDING" as const,
                current_version_id: versionId,
                current_version_number: versionNumber,
                created_at: now,
                updated_at: now,
                archived_at: null,
              };
          const version = {
            id: versionId,
            quote_id: quoteId,
            version_number: versionNumber,
            status: "DRAFT" as QuoteStatus,
            title: input.title,
            description: input.description,
            currency: input.currency,
            tax_rate: input.tax_rate,
            valid_until: input.valid_until,
            payment_terms: input.payment_terms,
            client_notes: input.client_notes,
            sent_at: null,
            approved_by: null,
            approved_at: null,
            rejected_by: null,
            rejected_at: null,
            rejection_reason: null,
            created_at: previousVersion?.created_at ?? now,
          };
          const items = input.items.map((item, index) => ({
            id: item.id ?? crypto.randomUUID(),
            quote_version_id: versionId,
            description: item.description,
            quantity: Number(item.quantity),
            unit_price: Number(item.unit_price),
            amount: Number(item.quantity) * Number(item.unit_price),
            sort_order: index,
          }));
          persistLocal({
            ...data,
            quotes: existing
              ? data.quotes.map((item) => (item.id === quoteId ? quote : item))
              : [quote, ...data.quotes],
            quoteVersions: previousVersion
              ? data.quoteVersions.map((item) =>
                  item.id === versionId ? version : item,
                )
              : [version, ...data.quoteVersions],
            quoteItems: [
              ...data.quoteItems.filter(
                (item) => item.quote_version_id !== versionId,
              ),
              ...items,
            ],
          });
        } else if (supabase) {
          const { error: quoteError } = await supabase.rpc("save_quote_draft", {
            p_quote_id: input.quote_id ?? null,
            p_project_id: data.project.id,
            p_change_request_id: input.change_request_id ?? null,
            p_title: input.title,
            p_description: input.description,
            p_currency: input.currency,
            p_tax_rate: input.tax_rate,
            p_valid_until: input.valid_until,
            p_payment_terms: input.payment_terms,
            p_client_notes: input.client_notes,
            p_items: input.items,
          });
          if (quoteError) throw quoteError;
          await refresh();
        }
        setNotice("Borrador guardado.");
        return true;
      } catch (reason) {
        setNotice(
          reason instanceof Error
            ? reason.message
            : "No se pudo guardar la cotización.",
        );
        return false;
      }
    },
    [data, localDemo, persistLocal, refresh],
  );

  const sendQuote = useCallback(
    async (quoteId: string, notify: boolean, recipients: string[]) => {
      if (!data || data.viewer.role !== "POSITRONICO_OWNER") return false;
      try {
        let number = "";
        if (localDemo) {
          const quote = data.quotes.find((item) => item.id === quoteId);
          if (!quote) return false;
          const version = data.quoteVersions.find(
            (item) => item.id === quote.current_version_id,
          );
          if (!version || version.status !== "DRAFT")
            throw new Error("Sólo se puede enviar un borrador.");
          number =
            quote.quote_number ??
            `COT-${new Date().getFullYear()}-${String(data.quotes.filter((item) => item.quote_number).length + 1).padStart(3, "0")}`;
          const now = new Date().toISOString();
          const nextStatus = applyQuoteSend(version.status);
          const deliveries = notify
            ? recipients.map((recipient) => ({
                id: crypto.randomUUID(),
                quote_id: quoteId,
                event_type: "QUOTE_SENT",
                recipient,
                status: "SENT" as const,
                sent_at: now,
                error_message: null,
                created_at: now,
              }))
            : [];
          persistLocal({
            ...data,
            quotes: data.quotes.map((item) =>
              item.id === quoteId
                ? {
                    ...item,
                    quote_number: number,
                    status: nextStatus,
                    updated_at: now,
                  }
                : item,
            ),
            quoteVersions: data.quoteVersions.map((item) =>
              item.id === version.id
                ? { ...item, status: nextStatus, sent_at: now }
                : item,
            ),
            quoteEvents: [
              {
                id: crypto.randomUUID(),
                quote_id: quoteId,
                quote_version_id: version.id,
                event_type: "SENT",
                request_type: null,
                comment: "",
                actor_name: data.viewer.full_name,
                created_at: now,
              },
              ...data.quoteEvents,
            ],
            emailDeliveries: [...deliveries, ...data.emailDeliveries],
          });
        } else if (supabase) {
          const { data: result, error: sendError } = await supabase.rpc(
            "send_quote",
            { p_quote_id: quoteId },
          );
          if (sendError) throw sendError;
          number = typeof result === "string" ? result : "";
          if (notify && recipients.length) {
            const token = getSupabaseAccessToken();
            const apiUrl = import.meta.env.VITE_API_URL?.replace(/\/$/, "");
            if (!token || !apiUrl)
              throw new Error(
                "La cotización se envió, pero falta configurar el servicio de email.",
              );
            const response = await fetch(
              `${apiUrl}/api/quotes/${quoteId}/notifications`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ recipients }),
              },
            );
            if (!response.ok)
              throw new Error(
                "La cotización se envió, pero la notificación por email falló.",
              );
          }
          await refresh();
        }
        setNotice(
          `${number || "Cotización"} enviada${notify && recipients.length ? " y notificada" : ""}.`,
        );
        return true;
      } catch (reason) {
        setNotice(
          reason instanceof Error
            ? reason.message
            : "No se pudo enviar la cotización.",
        );
        return false;
      }
    },
    [data, localDemo, persistLocal, refresh],
  );

  const respondToQuote = useCallback(
    async (
      quoteId: string,
      response: QuoteResponse,
      comment: string,
      requestType?: string,
    ) => {
      if (!data || data.viewer.role !== "CLIENT_ADMIN") return false;
      if (response !== "APPROVED" && !comment.trim()) {
        setNotice("El comentario es obligatorio.");
        return false;
      }
      try {
        if (localDemo) {
          const quote = data.quotes.find((item) => item.id === quoteId);
          if (!quote)
            throw new Error("Esta cotización ya no admite una respuesta.");
          applyQuoteResponse(data.viewer.role, quote.status, response, comment);
          const now = new Date().toISOString();
          persistLocal({
            ...data,
            quotes: data.quotes.map((item) =>
              item.id === quoteId
                ? { ...item, status: response, updated_at: now }
                : item,
            ),
            quoteVersions: data.quoteVersions.map((item) =>
              item.id === quote.current_version_id
                ? {
                    ...item,
                    status: response,
                    approved_by:
                      response === "APPROVED" ? data.viewer.id : null,
                    approved_at: response === "APPROVED" ? now : null,
                    rejected_by:
                      response === "REJECTED" ? data.viewer.id : null,
                    rejected_at: response === "REJECTED" ? now : null,
                    rejection_reason: response === "REJECTED" ? comment : null,
                  }
                : item,
            ),
            quoteEvents: [
              {
                id: crypto.randomUUID(),
                quote_id: quoteId,
                quote_version_id: quote.current_version_id,
                event_type: response,
                request_type:
                  response === "CHANGES_REQUESTED"
                    ? (requestType ?? "Otro")
                    : null,
                comment,
                actor_name: data.viewer.full_name,
                created_at: now,
              },
              ...data.quoteEvents,
            ],
          });
        } else if (supabase) {
          const { error: responseError } = await supabase.rpc(
            "respond_to_quote",
            {
              p_quote_id: quoteId,
              p_response: response,
              p_comment: comment,
              p_request_type: requestType ?? null,
            },
          );
          if (responseError) throw responseError;
          await refresh();
        }
        setNotice(
          response === "APPROVED"
            ? "Cotización aprobada."
            : response === "REJECTED"
              ? "Cotización rechazada."
              : "Cambios solicitados.",
        );
        return true;
      } catch (reason) {
        setNotice(
          reason instanceof Error
            ? reason.message
            : "No se pudo registrar la respuesta.",
        );
        return false;
      }
    },
    [data, localDemo, persistLocal, refresh],
  );

  const createQuoteRevision = useCallback(
    async (quoteId: string) => {
      if (!data || data.viewer.role !== "POSITRONICO_OWNER") return false;
      try {
        if (localDemo) {
          const quote = data.quotes.find((item) => item.id === quoteId);
          if (!quote || quote.status === "DRAFT") return false;
          const source = data.quoteVersions.find(
            (item) => item.id === quote.current_version_id,
          );
          if (!source) return false;
          const now = new Date().toISOString();
          const versionId = crypto.randomUUID();
          const versionNumber = quote.current_version_number + 1;
          const version = {
            ...source,
            id: versionId,
            version_number: versionNumber,
            status: "DRAFT" as QuoteStatus,
            sent_at: null,
            approved_by: null,
            approved_at: null,
            rejected_by: null,
            rejected_at: null,
            rejection_reason: null,
            created_at: now,
          };
          const items = data.quoteItems
            .filter((item) => item.quote_version_id === source.id)
            .map((item) => ({
              ...item,
              id: crypto.randomUUID(),
              quote_version_id: versionId,
            }));
          persistLocal({
            ...data,
            quotes: data.quotes.map((item) =>
              item.id === quoteId
                ? {
                    ...item,
                    current_version_id: versionId,
                    current_version_number: versionNumber,
                    updated_at: now,
                  }
                : item,
            ),
            quoteVersions: [version, ...data.quoteVersions],
            quoteItems: [...items, ...data.quoteItems],
            quoteEvents: [
              {
                id: crypto.randomUUID(),
                quote_id: quoteId,
                quote_version_id: versionId,
                event_type: "REVISION_CREATED",
                request_type: null,
                comment: `Versión ${versionNumber}`,
                actor_name: data.viewer.full_name,
                created_at: now,
              },
              ...data.quoteEvents,
            ],
          });
        } else if (supabase) {
          const { error: revisionError } = await supabase.rpc(
            "create_quote_revision",
            { p_quote_id: quoteId },
          );
          if (revisionError) throw revisionError;
          await refresh();
        }
        setNotice("Nueva revisión creada como borrador.");
        return true;
      } catch (reason) {
        setNotice(
          reason instanceof Error
            ? reason.message
            : "No se pudo crear la revisión.",
        );
        return false;
      }
    },
    [data, localDemo, persistLocal, refresh],
  );

  const registerPayment = useCallback(
    async (quoteId: string, payment: PaymentInput) => {
      if (
        !data ||
        data.viewer.role !== "POSITRONICO_OWNER" ||
        payment.amount <= 0
      )
        return false;
      try {
        if (localDemo) {
          const quote = data.quotes.find((item) => item.id === quoteId);
          if (!quote || quote.status !== "APPROVED")
            throw new Error(
              "Sólo se registran pagos sobre cotizaciones aprobadas.",
            );
          const version = data.quoteVersions.find(
            (item) => item.id === quote.current_version_id,
          );
          if (!version) return false;
          const items = data.quoteItems.filter(
            (item) => item.quote_version_id === version.id,
          );
          const total = calculateQuoteTotals(items, version.tax_rate).total;
          const existing = data.payments.filter(
            (item) => item.quote_id === quoteId,
          );
          if (
            existing.reduce((sum, item) => sum + item.amount, 0) +
              payment.amount >
            total
          )
            throw new Error("El pago supera el saldo pendiente.");
          const created = {
            id: crypto.randomUUID(),
            quote_id: quoteId,
            ...payment,
            created_at: new Date().toISOString(),
          };
          const state = calculatePaymentState(total, [...existing, created]);
          persistLocal({
            ...data,
            payments: [created, ...data.payments],
            quotes: data.quotes.map((item) =>
              item.id === quoteId
                ? {
                    ...item,
                    payment_status: state.status,
                    updated_at: new Date().toISOString(),
                  }
                : item,
            ),
          });
        } else if (supabase) {
          const { error: paymentError } = await supabase.rpc(
            "register_quote_payment",
            {
              p_quote_id: quoteId,
              p_amount: payment.amount,
              p_payment_date: payment.payment_date,
              p_method: payment.method,
              p_reference: payment.reference,
              p_notes: payment.notes,
            },
          );
          if (paymentError) throw paymentError;
          await refresh();
        }
        setNotice("Pago registrado.");
        return true;
      } catch (reason) {
        setNotice(
          reason instanceof Error
            ? reason.message
            : "No se pudo registrar el pago.",
        );
        return false;
      }
    },
    [data, localDemo, persistLocal, refresh],
  );

  const archiveQuote = useCallback(
    async (quoteId: string) => {
      if (!data || data.viewer.role !== "POSITRONICO_OWNER") return false;
      try {
        if (localDemo)
          persistLocal({
            ...data,
            quotes: data.quotes.map((quote) =>
              quote.id === quoteId
                ? { ...quote, archived_at: new Date().toISOString() }
                : quote,
            ),
          });
        else if (supabase) {
          const { error: archiveError } = await supabase.rpc("archive_quote", {
            p_quote_id: quoteId,
          });
          if (archiveError) throw archiveError;
          await refresh();
        }
        setNotice("Cotización archivada.");
        return true;
      } catch (reason) {
        setNotice(
          reason instanceof Error
            ? reason.message
            : "No se pudo archivar la cotización.",
        );
        return false;
      }
    },
    [data, localDemo, persistLocal, refresh],
  );

  const adminSetQuoteStatus = useCallback(
    async (quoteId: string, status: QuoteStatus, reason: string) => {
      if (!data || data.viewer.role !== "POSITRONICO_OWNER" || !reason.trim())
        return false;
      try {
        if (localDemo)
          persistLocal({
            ...data,
            quotes: data.quotes.map((quote) =>
              quote.id === quoteId
                ? { ...quote, status, updated_at: new Date().toISOString() }
                : quote,
            ),
            quoteEvents: [
              {
                id: crypto.randomUUID(),
                quote_id: quoteId,
                quote_version_id:
                  data.quotes.find((quote) => quote.id === quoteId)
                    ?.current_version_id ?? null,
                event_type: "ADMIN_STATUS_OVERRIDE",
                request_type: null,
                comment: reason.trim(),
                actor_name: data.viewer.full_name,
                created_at: new Date().toISOString(),
              },
              ...data.quoteEvents,
            ],
          });
        else if (supabase) {
          const { error: statusError } = await supabase.rpc(
            "admin_set_quote_status",
            { p_quote_id: quoteId, p_status: status, p_reason: reason.trim() },
          );
          if (statusError) throw statusError;
          await refresh();
        }
        setNotice("Estado actualizado con auditoría.");
        return true;
      } catch (reasonError) {
        setNotice(
          reasonError instanceof Error
            ? reasonError.message
            : "No se pudo cambiar el estado.",
        );
        return false;
      }
    },
    [data, localDemo, persistLocal, refresh],
  );

  const downloadQuotePdf = useCallback(
    async (quoteId: string, quoteNumber?: string | null) => {
      try {
        const token = getSupabaseAccessToken();
        const apiUrl = import.meta.env.VITE_API_URL?.replace(/\/$/, "");
        if (localDemo) {
          setNotice("Conecta la API para descargar el PDF comercial.");
          return false;
        }
        if (!token || !apiUrl)
          throw new Error("La sesión o la API no están disponibles.");
        const response = await fetch(`${apiUrl}/api/quotes/${quoteId}/pdf`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok)
          throw new Error(
            ((await response.json().catch(() => ({}))) as { detail?: string })
              .detail ?? "No se pudo generar el PDF.",
          );
        const url = URL.createObjectURL(await response.blob());
        const link = document.createElement("a");
        link.href = url;
        link.download = `${quoteNumber ?? "cotizacion"}.pdf`;
        link.click();
        URL.revokeObjectURL(url);
        return true;
      } catch (reason) {
        setNotice(
          reason instanceof Error
            ? reason.message
            : "No se pudo descargar el PDF.",
        );
        return false;
      }
    },
    [localDemo],
  );

  const setEmailNotificationsEnabled = useCallback(
    async (enabled: boolean) => {
      if (!data || data.viewer.role !== "POSITRONICO_OWNER") return false;
      try {
        if (localDemo)
          persistLocal({ ...data, emailNotificationsEnabled: enabled });
        else if (supabase) {
          const { error: settingError } = await supabase.rpc(
            "set_email_notifications_enabled",
            { p_project_id: data.project.id, p_enabled: enabled },
          );
          if (settingError) throw settingError;
          await refresh();
        }
        setNotice(
          enabled
            ? "Notificaciones por email activadas."
            : "Notificaciones por email desactivadas.",
        );
        return true;
      } catch (reason) {
        setNotice(
          reason instanceof Error
            ? reason.message
            : "No se pudo actualizar la configuración.",
        );
        return false;
      }
    },
    [data, localDemo, persistLocal, refresh],
  );

  const uploadFile = useCallback(
    async (
      file: File,
      category: string,
      description: string,
      changeRequestId?: string | null,
      folderId?: string | null,
    ) => {
      if (!data) return false;
      const validation = validateProjectFile(file);
      if (!validation.ok) {
        setNotice(validation.message);
        return false;
      }
      try {
        const contentValidation = await validateProjectFileContents(file);
        if (!contentValidation.ok) {
          setNotice(contentValidation.message);
          return false;
        }
        const mimeType = projectFileMimeType(file);
        if (localDemo)
          persistLocal({
            ...data,
            files: [
              {
                id: crypto.randomUUID(),
                project_id: data.project.id,
                change_request_id: changeRequestId ?? null,
                folder_id: folderId ?? null,
                name: file.name,
                category,
                description,
                size_bytes: file.size,
                mime_type: mimeType,
                created_at: new Date().toISOString(),
                storage_path: `local/${file.name}`,
                updated_at: new Date().toISOString(),
                uploaded_by: data.viewer.id,
                uploader_name: data.viewer.full_name,
                archived_at: null,
                effective_archived: false,
                upload_status: "READY",
                location_path: [],
              },
              ...data.files,
            ],
          });
        else if (supabase) {
          const begun = await supabase.rpc("begin_project_file_upload", {
            p_project_id: data.project.id,
            p_folder_id: folderId ?? null,
            p_name: file.name,
            p_category: category,
            p_description: description,
            p_size_bytes: file.size,
            p_mime_type: mimeType,
            p_change_request_id: changeRequestId ?? null,
          });
          if (begun.error) throw begun.error;
          const plan = begun.data as { id?: unknown; storage_path?: unknown };
          if (
            typeof plan?.id !== "string" ||
            typeof plan?.storage_path !== "string"
          )
            throw new Error("Supabase no devolvió un contexto de subida válido.");

          let objectMayExist = false;
          try {
            const uploaded = await supabase.storage
              .from("project-files")
              .upload(plan.storage_path, file, {
                contentType: mimeType,
                upsert: false,
              });
            objectMayExist = uploaded.error === null;
            if (uploaded.error) throw uploaded.error;
            const finalized = await supabase.rpc(
              "finalize_project_file_upload",
              { p_file_id: plan.id },
            );
            if (finalized.error) throw finalized.error;
          } catch (uploadReason) {
            if (objectMayExist)
              await supabase.storage
                .from("project-files")
                .remove([plan.storage_path]);
            const cancelled = await supabase.rpc(
              "cancel_project_file_upload",
              { p_file_id: plan.id },
            );
            if (cancelled.error)
              throw new Error(
                "La subida quedó pendiente de conciliación. No se perdió el control del objeto; vuelve a intentarlo o contacta a soporte.",
              );
            throw uploadReason;
          }
          await refresh();
        }
        setNotice("Archivo subido.");
        return true;
      } catch (reason) {
        setNotice(
          reason instanceof Error
            ? reason.message
            : "No se pudo subir el archivo.",
        );
        return false;
      }
    },
    [data, localDemo, persistLocal, refresh],
  );

  const downloadFile = useCallback(
    async (storagePath: string, fileName?: string) => {
      try {
        if (localDemo) {
          setNotice(
            "La vista local guarda metadata; conecta Supabase para descargar el objeto.",
          );
          return false;
        }
        if (!supabase) return false;
        const { data: signed, error: signedError } = await supabase.storage
          .from("project-files")
          .createSignedUrl(
            storagePath,
            60,
            fileName ? { download: fileName } : undefined,
          );
        if (signedError) throw signedError;
        window.open(signed.signedUrl, "_blank", "noopener,noreferrer");
        return true;
      } catch (reason) {
        setNotice(
          reason instanceof Error
            ? reason.message
            : "No se pudo descargar el archivo.",
        );
        return false;
      }
    },
    [localDemo],
  );

  const inviteMember = useCallback(
    async (email: string, role: string) => {
      if (!data) return false;
      try {
        if (localDemo)
          persistLocal({
            ...data,
            members: [
              {
                id: crypto.randomUUID(),
                organization_id: data.organization.id,
                user_id: null,
                full_name: email.split("@")[0] ?? email,
                email,
                email_verified_at: null,
                role: role as WorkspaceData["members"][number]["role"],
                status: "INVITED" as const,
              },
              ...data.members,
            ],
          });
        else if (supabase) {
          const token = getSupabaseAccessToken();
          if (!token) throw new Error("La sesión expiró.");
          const apiUrl = import.meta.env.VITE_API_URL?.replace(/\/$/, "");
          if (!apiUrl)
            throw new Error("Configura VITE_API_URL para enviar invitaciones.");
          const response = await fetch(`${apiUrl}/api/invitations`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              email,
              organization_id: data.organization.id,
              project_id: data.project.id,
              role,
            }),
          });
          if (!response.ok)
            throw new Error(
              ((await response.json()) as { detail?: string }).detail ??
                "No se pudo enviar la invitación.",
            );
          await refresh();
        }
        setNotice("Invitación enviada.");
        return true;
      } catch (reason) {
        setNotice(
          reason instanceof Error
            ? reason.message
            : "No se pudo enviar la invitación.",
        );
        return false;
      }
    },
    [data, localDemo, persistLocal, refresh],
  );

  const value = useMemo(
    () => ({
      data,
      loading,
      error,
      notice,
      refresh,
      save,
      archive,
      setCollaborationArchived,
      deleteCollaborationRecord,
      moveWorkItem,
      completeWaitingClient,
      addWorkItemComment,
      addChangeRequestComment,
      setPhaseStatus,
      savePhase,
      saveQuoteDraft,
      sendQuote,
      respondToQuote,
      createQuoteRevision,
      registerPayment,
      archiveQuote,
      adminSetQuoteStatus,
      downloadQuotePdf,
      setEmailNotificationsEnabled,
      uploadFile,
      downloadFile,
      inviteMember,
      clearNotice: () => setNotice(null),
    }),
    [
      data,
      loading,
      error,
      notice,
      refresh,
      save,
      archive,
      setCollaborationArchived,
      deleteCollaborationRecord,
      moveWorkItem,
      completeWaitingClient,
      addWorkItemComment,
      addChangeRequestComment,
      setPhaseStatus,
      savePhase,
      saveQuoteDraft,
      sendQuote,
      respondToQuote,
      createQuoteRevision,
      registerPayment,
      archiveQuote,
      adminSetQuoteStatus,
      downloadQuotePdf,
      setEmailNotificationsEnabled,
      uploadFile,
      downloadFile,
      inviteMember,
    ],
  );
  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace() {
  const value = useContext(WorkspaceContext);
  if (!value)
    throw new Error("useWorkspace must be used inside WorkspaceProvider");
  return value;
}
